import {
  BIOSTIMULANT_STEP_GROUP_KEY,
  OPERATION_STEP_GROUP_KEY,
  PLANTPROTECTION_STEP_GROUP_KEY,
  PRODUCTION_STEP_GROUP_KEY,
  SEED_STEP_GROUP_KEY,
  SOILINPUT_STEP_GROUP_KEY,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_LABELS,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_PRODUCTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  TASKS_GRID_ADD_BUTTON_LABEL,
  TASKS_GRID_ALL_FILTER_LABEL,
  TASKS_GRID_HIDDEN_BY_FILTER_SUFFIX,
  TASKS_GRID_HIDDEN_INPUT_PLURAL,
  TASKS_GRID_HIDDEN_INPUT_SINGULAR,
} from 'constants/uiConstants';
import type { IInput } from 'interfaces/IInput';
import { IInputStep, InputStepType } from 'interfaces/IInputStep';

export const ALL_STEP_CATEGORY_KEY = 'all';

export interface StepCategoryFilter {
  key: string;
  label: string;
  stepTypes?: InputStepType[];
  groupKey?: string;
}

const categoryFilter = (
  key: string,
  stepTypes: InputStepType[],
  groupKey: string
): StepCategoryFilter => ({
  key,
  label: SUMMARY_ENTITY_LABELS[key],
  stepTypes,
  groupKey,
});

// The grid's filters use the same categories, keys, labels and order as
// Live totals (Insights/costCategories.ts COST_CATEGORY_ORDER), plus
// Production, so a filter can be shared with Live totals (OP-2534, OP-2535).
// Seed covers Seed and Seed Treatment, as it does in Live totals.
export const STEP_CATEGORY_FILTERS: StepCategoryFilter[] = [
  { key: ALL_STEP_CATEGORY_KEY, label: TASKS_GRID_ALL_FILTER_LABEL },
  categoryFilter(
    SUMMARY_ENTITY_SEED,
    [InputStepType.Seed, InputStepType.SeedDress],
    SEED_STEP_GROUP_KEY
  ),
  categoryFilter(
    SUMMARY_ENTITY_SOIL_INPUT,
    [InputStepType.SoilInput],
    SOILINPUT_STEP_GROUP_KEY
  ),
  categoryFilter(
    SUMMARY_ENTITY_PLANT_PROTECTION,
    [InputStepType.CropProtection, InputStepType.Adjuvant],
    PLANTPROTECTION_STEP_GROUP_KEY
  ),
  categoryFilter(
    SUMMARY_ENTITY_BIOSTIMULANT,
    [InputStepType.Biostimulant],
    BIOSTIMULANT_STEP_GROUP_KEY
  ),
  categoryFilter(
    SUMMARY_ENTITY_OPERATION,
    [InputStepType.Application, InputStepType.Generic],
    OPERATION_STEP_GROUP_KEY
  ),
  categoryFilter(
    SUMMARY_ENTITY_PRODUCTION,
    [InputStepType.Production],
    PRODUCTION_STEP_GROUP_KEY
  ),
];

// Derived from STEP_CATEGORY_FILTERS' own groupKey so the two lists can't drift apart.
export const ADD_INPUT_CATEGORY_KEY_BY_FILTER: Record<string, string> =
  STEP_CATEGORY_FILTERS.reduce<Record<string, string>>((map, filter) => {
    if (filter.groupKey) map[filter.key] = filter.groupKey;
    return map;
  }, {});

export const filterStepsByCategory = (
  steps: IInputStep[],
  categoryKey: string
): IInputStep[] => {
  const category = STEP_CATEGORY_FILTERS.find((c) => c.key === categoryKey);
  if (!category?.stepTypes) return steps;
  return steps.filter((step) =>
    (category.stepTypes ?? []).includes(step.stepType)
  );
};

export const getStepCategoryFilter = (
  categoryKey: string
): StepCategoryFilter | undefined =>
  STEP_CATEGORY_FILTERS.find((c) => c.key === categoryKey);

/**
 * The plans with only the steps a filter covers, for totals that follow the
 * grid's filter (OP-2535). Returns the plans unchanged with no filter.
 */
export const filterInputsToStepCategory = (
  inputs: IInput[],
  categoryKey: string | null
): IInput[] => {
  const stepTypes = categoryKey
    ? getStepCategoryFilter(categoryKey)?.stepTypes
    : undefined;
  if (!stepTypes) return inputs;
  return inputs.map((input) => ({
    ...input,
    tasks: input.tasks.map((task) => ({
      ...task,
      steps: (task.steps ?? []).filter((step) =>
        stepTypes.includes(step.stepType)
      ),
    })),
  }));
};

/** True when a filter is on and the task has at least one step it matches. */
export const taskMatchesStepCategory = (
  steps: IInputStep[],
  categoryKey: string
): boolean => {
  const stepTypes = getStepCategoryFilter(categoryKey)?.stepTypes;
  return !!stepTypes && steps.some((step) => stepTypes.includes(step.stepType));
};

/** The row's "+ Add" label under a filter, e.g. "Add soil input". */
export const getAddCategoryLabel = (categoryLabel: string): string =>
  `${TASKS_GRID_ADD_BUTTON_LABEL} ${categoryLabel.toLowerCase()}`;

export const getInputsHiddenByFilterMessage = (hiddenCount: number): string =>
  `${hiddenCount} ${
    hiddenCount === 1
      ? TASKS_GRID_HIDDEN_INPUT_SINGULAR
      : TASKS_GRID_HIDDEN_INPUT_PLURAL
  } ${TASKS_GRID_HIDDEN_BY_FILTER_SUFFIX}`;
